import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useSettings } from "@/context/SettingsContext";

const links = [
  { to: "/", label: "Home", tid: NAV.home, end: true },
  { to: "/chi-sono", label: "Chi Sono", tid: NAV.chiSono },
  { to: "/servizi", label: "Servizi", tid: NAV.servizi },
  { to: "/portfolio", label: "Portfolio", tid: NAV.portfolio },
  { to: "/shop", label: "Shop", tid: "nav-shop", shop: true },
  { to: "/shop-the-project", label: "Shop the Project", tid: "nav-shop-the-project", shop: true },
  { to: "/library", label: "Library", tid: "nav-library", library: true },
  { to: "/blog", label: "Diario", tid: "nav-blog" },
  { to: "/contatti", label: "Contatti", tid: NAV.contatti },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const visibleLinks = links.filter((l) => (!l.shop || settings.shop_visible) && (!l.library || settings.library_visible !== false));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-bone/90 backdrop-blur-md border-b border-hair" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6 h-20">
        {/* Logo orizzontale: mai a capo, mai compresso */}
        <Link to="/" data-testid={NAV.logo} className="flex flex-col leading-none shrink-0 w-[230px] min-[1360px]:w-[250px]">
          <span className="font-serif text-[22px] xl:text-2xl tracking-tight whitespace-nowrap">Giovanna Matino</span>
          <span className="eyebrow mt-1 whitespace-nowrap">Interior Design Studio</span>
        </Link>

        {/* Navigazione completa solo quando c'è spazio elegante (>=1360px): mai comprimere il logo */}
        <nav className="hidden min-[1360px]:flex items-center gap-6 2xl:gap-8">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.1em] uppercase whitespace-nowrap link-underline transition-colors ${
                  isActive ? "text-gold" : "text-ink/80 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA proporzionata + hamburger sotto i 1280px */}
        <div className="flex items-center gap-4 shrink-0">
          <Link
            to="/prenota"
            data-testid={NAV.ctaBook}
            className="hidden min-[1360px]:inline-flex items-center justify-center bg-ink text-bone px-6 min-h-[40px] text-[11px] tracking-[0.14em] uppercase whitespace-nowrap hover:bg-gold hover:text-ink transition-colors"
          >
            Prenota Consulenza
          </Link>
          <button
            className="min-[1360px]:hidden p-3 -mr-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setOpen(!open)}
            data-testid={NAV.mobileToggle}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile/Tablet menu */}
      {open && (
        <div className="min-[1360px]:hidden bg-bone border-t border-hair max-h-[calc(100dvh-5rem)] overflow-y-auto">
          <nav className="container-x py-6 flex flex-col">
            {visibleLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`${l.tid}-mobile`}
                className={({ isActive }) =>
                  `font-serif text-2xl py-2 min-h-[44px] flex items-center transition-colors ${isActive ? "text-gold" : "text-ink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 mt-2 border-t border-hair space-y-2">
              <Link to="/prenota" className="btn-ink w-full min-h-[48px]" data-testid="nav-cta-book-mobile">
                Prenota Consulenza
              </Link>
              <Link
                to="/shop-the-project#accesso"
                className="w-full min-h-[48px] inline-flex items-center justify-center text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors"
                data-testid="nav-area-cliente-mobile"
              >
                Area Cliente · Accedi
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
